import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { shareFittingRoom } from '../lib/api'
import { uiStrings } from '../lib/strings'

/**
 * Sharing her fitting room with a friend: a panel that drops down from the
 * header's share mark, with the view-only link written out, a button that
 * copies it, and — where the phone has one — its own share sheet.
 *
 * The link is a second key (`/v/…`), made the first time she opens this and
 * the same one every time after; never her own link, which edits her lists.
 */
export function ShareSheet({
  token,
  boutique,
  locale,
  onClose,
}: {
  readonly token: string
  readonly boutique: string
  readonly locale: string
  readonly onClose: () => void
}) {
  const t = uiStrings(locale)
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)
  const field = useRef<HTMLInputElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const link = useMutation({
    mutationFn: () => shareFittingRoom(token),
  })
  const make = link.mutate
  useEffect(() => {
    make()
  }, [make])

  // On whatever origin this page is on, so a link made on a preview host opens there too.
  const url = link.data ? new URL(new URL(link.data.url).pathname, window.location.origin).toString() : ''
  const canShare = typeof navigator.share === 'function'

  useEffect(() => {
    panel.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      return
    } catch {
      // The clipboard API refused (no permission, or not a secure page): the older way, from the field itself.
    }
    field.current?.select()
    let done = false
    try {
      done = document.execCommand('copy')
    } catch {
      done = false
    }
    setCopied(done)
    setCopyFailed(!done)
  }
  const share = async () => {
    try {
      await navigator.share({ title: t.shareTitle, text: t.shareMessage(boutique), url })
    } catch {
      // Closed without sending: nothing to say.
    }
  }

  return (
    <div className="fixed inset-0 z-50" role="presentation">
      <button type="button" className="fr-sheet-backdrop absolute inset-0 h-full w-full cursor-default" aria-label={t.close} onClick={onClose} />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fr-share-title"
        className="fr-sheet-down relative mx-auto w-full max-w-[520px] px-5 pt-4 pb-6"
        style={{ background: 'var(--fr-ground-deep)', borderBottom: '1px solid var(--fr-rule)' }}
        data-testid="share-sheet"
      >
        <div className="flex items-start gap-3">
          <h2 id="fr-share-title" className="fr-display flex-1 text-[1.375rem] leading-tight">
            {t.shareTitle}
          </h2>
          <button type="button" className="fr-iconbtn shrink-0" aria-label={t.close} title={t.close} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        <p className="mt-2 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
          {t.shareBody}
        </p>

        <div className="mt-4 flex items-stretch gap-2">
          <input
            ref={field}
            className="fr-input min-w-0 flex-1 text-[0.875rem]"
            readOnly
            aria-label={t.shareLink}
            value={link.isPending ? t.shareMaking : url}
            onFocus={(event) => event.currentTarget.select()}
            data-testid="share-url"
          />
          <button type="button" className="fr-btn fr-btn-outline shrink-0" disabled={!url} onClick={() => void copy()} data-testid="share-copy">
            {copied ? t.shareCopiedShort : t.shareCopy}
          </button>
        </div>
        <p className="mt-1 min-h-[1.25rem] text-[0.8125rem]" role="status" style={{ color: 'var(--fr-ink-faint)' }}>
          {link.isError ? t.shareFailed : copied ? t.shareLinkCopied : copyFailed ? t.shareCopyByHand : ''}
        </p>

        {canShare ? (
          <button type="button" className="fr-btn fr-btn-primary mt-2 w-full" disabled={!url} onClick={() => void share()}>
            {t.shareButton}
          </button>
        ) : null}
      </div>
    </div>
  )
}
